/**
 * <product-recommendations> — custom element used in tyt-related-products.liquid.
 *
 * Why this exists: Shopify's `product.recommendations` object is intentionally empty
 * on the initial page render. Recommendations only populate when the section is
 * fetched via the Section Rendering API at routes.product_recommendations_url
 * (with section_id, product_id, limit query params).
 *
 * This element:
 *   1. Waits until it scrolls into view (saves a fetch if the user never reaches it).
 *   2. Fetches its own data-url, which returns the full rendered section with
 *      recommendations.performed = true and the cards populated.
 *   3. Parses the response, finds the matching <product-recommendations> element
 *      in the returned HTML, and swaps the inner content into this element.
 *
 * If the fetch fails or returns no recommendations, the element stays empty —
 * the section's heading + link still show, just no cards underneath. Graceful.
 */
class TYTProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url) return;

    const observer = new IntersectionObserver((entries, obs) => {
      if (!entries[0].isIntersecting) return;
      obs.unobserve(this);
      this.loadRecommendations();
    }, { rootMargin: '0px 0px 400px 0px' });

    observer.observe(this);
  }

  loadRecommendations() {
    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('[tyt-product-recommendations] HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('product-recommendations');
        if (!fresh) return;
        const cards = fresh.innerHTML.trim();
        if (cards.length === 0) return;
        this.innerHTML = cards;
      })
      .catch((err) => {
        console.warn('[tyt-product-recommendations] fetch failed', err);
      });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', TYTProductRecommendations);
}
